export type BlisterCorrosionSection = {
  notApplicable: boolean
  surfacePainted: number | null
  componentObstructedBy: string
  inputComponentObstructedBy: boolean
  blisteringSize: number | null
  blisteringDensity: number | null
  brokenBlistersPresent: string
  unacceptableBlistering: number | null
  corrosion: number | null
  corrosionDistribution: string
  localCorrosionSqFt: number | null
  corrosionPlusBlistering: number | null
  coatingCondition: string
}

export type StructuralIntegritySection = {
  notApplicable: boolean
  asFoundStructuralGrade: string
  defaultRisk: string
  structuralIssuesPresent: boolean
  utGaugingPerformed: boolean
  structuralIssues: string[]
}

export class G1N5 {
  // Surveyor & General Data
  organization: string
  surveyorName: string
  contactInformation: string
  ship: string
  hull: string
  tankVoidNumber: string
  wcrEquip: string
  date: string
  surveyReason: string
  jcn: string
  pValue: number | null
  sValue: number | null
  rValue: number | null
  tankVoidService: string

  // General Data Section
  paintedSurfaceArea: number | null
  solidBallastPresent: boolean
  apl: string

  // Penetration Data
  manholeCoverCondition: string
  manholeCoverBrokenStuds: string
  manholeCoverSealFailure: string
  otherPenetrationCondition: string
  stuffingTubeNotCapped: string
  stuffingTubeNotSealed: string

  // Ladder Data
  laddersPresent: boolean
  laddersDamaged: boolean
  laddersType: string
  laddersMaterial: string

  // Tank Level Indicator (TLI)
  tliPresent: boolean
  tliType: string
  tliDamaged: boolean
  bareDefectiveWiring: string
  damagedComponents: string

  // Sounding Tube Data
  soundingTubePresent: boolean
  stuckPlumbBob: string
  strikerPlateStatus: string
  wearForStrikerPlate: number | null

  // Cathodic Protection
  sacrificialAnodesPresent: boolean
  numberOfAnodesDepleted: number | null

  // Piping Valve Data
  pipingPresent: boolean
  pipingLeaks: string
  pipingVisuallyDamaged: string
  pipingSupportsDamaged: boolean
  valvePresent: boolean
  valveLeaks: string
  valveVisuallyDamaged: string
  suctionBellmouthPresent: boolean
  wasterPlatePresent: boolean
  wearForWasterPlate: number | null

  // Blister Coating and Corrosion Data
  isTankCres: boolean
  corrosionIssuesPresent: string[]

  // Blister/Corrosion Sections (nested)
  top: BlisterCorrosionSection
  sides: BlisterCorrosionSection
  stiffeners: BlisterCorrosionSection
  bottom: BlisterCorrosionSection

  // Structural Integrity Sections (nested)
  structuralTop: StructuralIntegritySection
  structuralSides: StructuralIntegritySection
  structuralStiffeners: StructuralIntegritySection
  structuralBottom: StructuralIntegritySection

  // Additional Information
  numberOfPicturesTaken: number | null
  pictureFileNumbers: string
  comments: string

  // N/A Section Flags
  tliNotApplicable: boolean
  ladderNotApplicable: boolean
  soundingTubeNotApplicable: boolean
  cathodicProtectionNotApplicable: boolean
  pipingValveNotApplicable: boolean
  structuralDataNotApplicable: boolean

  constructor() {
    // Initialize all fields with default values
    this.organization = ''
    this.surveyorName = ''
    this.contactInformation = ''
    this.ship = ''
    this.hull = ''
    this.tankVoidNumber = ''
    this.wcrEquip = ''
    this.date = ''
    this.surveyReason = ''
    this.jcn = ''
    this.pValue = null
    this.sValue = null
    this.rValue = null
    this.tankVoidService = ''

    this.paintedSurfaceArea = null
    this.solidBallastPresent = false
    this.apl = ''

    this.manholeCoverCondition = ''
    this.manholeCoverBrokenStuds = ''
    this.manholeCoverSealFailure = ''
    this.otherPenetrationCondition = ''
    this.stuffingTubeNotCapped = ''
    this.stuffingTubeNotSealed = ''

    this.laddersPresent = false
    this.laddersDamaged = false
    this.laddersType = ''
    this.laddersMaterial = ''

    this.tliPresent = false
    this.tliType = ''
    this.tliDamaged = false
    this.bareDefectiveWiring = ''
    this.damagedComponents = ''

    this.soundingTubePresent = false
    this.stuckPlumbBob = ''
    this.strikerPlateStatus = ''
    this.wearForStrikerPlate = null

    this.sacrificialAnodesPresent = false
    this.numberOfAnodesDepleted = null

    this.pipingPresent = false
    this.pipingLeaks = ''
    this.pipingVisuallyDamaged = ''
    this.pipingSupportsDamaged = false
    this.valvePresent = false
    this.valveLeaks = ''
    this.valveVisuallyDamaged = ''
    this.suctionBellmouthPresent = false
    this.wasterPlatePresent = false
    this.wearForWasterPlate = null

    this.isTankCres = false
    this.corrosionIssuesPresent = []

    // Blister/Corrosion Sections
    this.top = {
      notApplicable: false,
      surfacePainted: null,
      componentObstructedBy: '',
      inputComponentObstructedBy: false,
      blisteringSize: null,
      blisteringDensity: null,
      brokenBlistersPresent: '',
      unacceptableBlistering: null,
      corrosion: null,
      corrosionDistribution: '',
      localCorrosionSqFt: null,
      corrosionPlusBlistering: null,
      coatingCondition: '',
    }
    this.sides = {
      notApplicable: false,
      surfacePainted: null,
      componentObstructedBy: '',
      inputComponentObstructedBy: false,
      blisteringSize: null,
      blisteringDensity: null,
      brokenBlistersPresent: '',
      unacceptableBlistering: null,
      corrosion: null,
      corrosionDistribution: '',
      localCorrosionSqFt: null,
      corrosionPlusBlistering: null,
      coatingCondition: '',
    }
    this.stiffeners = {
      notApplicable: false,
      surfacePainted: null,
      componentObstructedBy: '',
      inputComponentObstructedBy: false,
      blisteringSize: null,
      blisteringDensity: null,
      brokenBlistersPresent: '',
      unacceptableBlistering: null,
      corrosion: null,
      corrosionDistribution: '',
      localCorrosionSqFt: null,
      corrosionPlusBlistering: null,
      coatingCondition: '',
    }
    this.bottom = {
      notApplicable: false,
      surfacePainted: null,
      componentObstructedBy: '',
      inputComponentObstructedBy: false,
      blisteringSize: null,
      blisteringDensity: null,
      brokenBlistersPresent: '',
      unacceptableBlistering: null,
      corrosion: null,
      corrosionDistribution: '',
      localCorrosionSqFt: null,
      corrosionPlusBlistering: null,
      coatingCondition: '',
    }

    // Structural Integrity Sections
    this.structuralTop = {
      notApplicable: false,
      asFoundStructuralGrade: '',
      defaultRisk: '',
      structuralIssuesPresent: false,
      utGaugingPerformed: false,
      structuralIssues: [],
    }
    this.structuralSides = {
      notApplicable: false,
      asFoundStructuralGrade: '',
      defaultRisk: '',
      structuralIssuesPresent: false,
      utGaugingPerformed: false,
      structuralIssues: [],
    }
    this.structuralStiffeners = {
      notApplicable: false,
      asFoundStructuralGrade: '',
      defaultRisk: '',
      structuralIssuesPresent: false,
      utGaugingPerformed: false,
      structuralIssues: [],
    }
    this.structuralBottom = {
      notApplicable: false,
      asFoundStructuralGrade: '',
      defaultRisk: '',
      structuralIssuesPresent: false,
      utGaugingPerformed: false,
      structuralIssues: [],
    }

    this.numberOfPicturesTaken = null
    this.pictureFileNumbers = ''
    this.comments = ''

    // N/A Section Flags
    this.tliNotApplicable = false
    this.ladderNotApplicable = false
    this.soundingTubeNotApplicable = false
    this.cathodicProtectionNotApplicable = false
    this.pipingValveNotApplicable = false
    this.structuralDataNotApplicable = false
  }

  static fromJSON(json: Record<string, unknown>): G1N5 {
    const g1n5 = new G1N5()

    // Map all fields from JSON
    Object.keys(json).forEach((key) => {
      if (key in g1n5) {
        ;(g1n5 as unknown as Record<string, unknown>)[key] = json[key]
      }
    })

    return g1n5
  }

  static toJSON(g1n5: G1N5): Record<string, unknown> {
    return { ...g1n5 }
  }
}
